import { useAlert } from '@inbody/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { v2QueryKeys } from './queryKeys';
import { CartItem, CartItemsBody } from './schemas';
import { useV2CartApis } from './useV2CartApis';

import { isShownByGlobalToast } from '@/pages/v2/lib';

/** N14 — 장바구니 조회 (FileResponse[] 배열 그대로) */
export const useCart = (enabled = true) => {
  const { getCartAPI } = useV2CartApis();
  return useQuery({
    queryKey: v2QueryKeys.cart(),
    queryFn: () => getCartAPI(),
    enabled,
  });
};

/**
 * N14 — 장바구니 담기. 결과는 토스트 1개로 합친다.
 * - 이미 담긴 파일(다른 경로·별칭에서 담은 같은 파일 포함)은 건너뛴 개수를 알린다(CART-31).
 *   개수는 헤더 배지가 들고 있는 장바구니 캐시로 센다 — 요청은 그대로 보낸다(서버가 중복을 막음)
 * - 호출부가 넘긴 폴더 제외 개수(폴더는 담을 수 없음)를 같은 토스트에 붙인다(ARC-81)
 */
export const useAddCartItems = () => {
  const { postCartItemsAPI } = useV2CartApis();
  const { toastAlert } = useAlert();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      fileIds,
      excludedFolderCount = 0,
    }: CartItemsBody & { excludedFolderCount?: number }) => {
      const inCart = new Set(
        (queryClient.getQueryData<CartItem[]>(v2QueryKeys.cart()) ?? []).map(
          ({ id }) => id,
        ),
      );
      const alreadyCount = fileIds.filter((id) => inCart.has(id)).length;
      await postCartItemsAPI({ fileIds });
      return {
        addedCount: fileIds.length - alreadyCount,
        alreadyCount,
        excludedFolderCount,
      };
    },
    onSuccess: ({ addedCount, alreadyCount, excludedFolderCount }) => {
      queryClient.invalidateQueries({ queryKey: v2QueryKeys.cart() });
      const folderNote =
        excludedFolderCount > 0
          ? ' 폴더는 장바구니에 담을 수 없어 제외했습니다.'
          : '';
      if (addedCount === 0) {
        toastAlert.warning(
          `${alreadyCount === 1 ? '이미 장바구니에 있는 파일입니다.' : `선택한 파일 ${alreadyCount}개는 이미 장바구니에 있습니다.`}${folderNote}`,
        );
        return;
      }
      const alreadyNote =
        alreadyCount > 0
          ? ` 이미 장바구니에 있는 파일 ${alreadyCount}개는 건너뛰었습니다.`
          : '';
      toastAlert.success(`장바구니에 담았습니다.${alreadyNote}${folderNote}`);
    },
    onError: (error) => {
      if (!isShownByGlobalToast(error))
        toastAlert.error('장바구니 담기에 실패했습니다.');
    },
  });
};

/**
 * N14 — 장바구니 빼기.
 * silent=true는 다운로드 완료 후 자동 정리처럼 사용자가 직접 누르지 않은 경우 —
 * 목록 무효화는 그대로 하되 토스트만 띄우지 않는다.
 */
export const useRemoveCartItems = ({ silent = false } = {}) => {
  const { deleteCartItemsAPI } = useV2CartApis();
  const { toastAlert } = useAlert();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CartItemsBody) => deleteCartItemsAPI(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2QueryKeys.cart() });
      if (!silent) toastAlert.success('장바구니에서 뺐습니다.');
    },
    onError: () => {
      if (!silent) toastAlert.error('장바구니 빼기에 실패했습니다.');
    },
  });
};

/**
 * N15 — 장바구니 ZIP 요청 → jobId.
 * 진행 추적은 N13과 동일하게 useDownloadJob(jobId)을 재사용한다.
 * 전체 선택이든 일부 선택이든 고른 파일 id를 보낸다(F7 · 백엔드 #6).
 */
export const useRequestCartDownload = () => {
  const { postCartDownloadAPI } = useV2CartApis();
  const { toastAlert } = useAlert();

  return useMutation({
    mutationFn: (body: CartItemsBody) => postCartDownloadAPI(body),
    // N13(useRequestBulkDownload)과 동일한 안내 — 권한 제외분은 완료 토스트에 합친다(CART-07)
    onSuccess: (data) => {
      if (data.status === 'Empty')
        toastAlert.warning(
          data.skippedCount > 0
            ? `다운로드할 수 있는 항목이 없습니다. 권한이 없는 ${data.skippedCount}개 항목은 제외했습니다.`
            : '다운로드할 수 있는 항목이 없습니다.',
        );
    },
    onError: (error) => {
      if (!isShownByGlobalToast(error))
        toastAlert.error('장바구니 다운로드 요청에 실패했습니다.');
    },
  });
};
